import { cloneDeep } from 'lodash';
import maplibregl, { FillLayerSpecification, GeoJSONSourceSpecification } from 'maplibre-gl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import useLayerHover from '@/hooks/map-box/useLayerHover';
import useLayerToolTipHover from '@/hooks/map-box/useLayerToolTipHover';
import useDebounce from '@/hooks/useDebounce';
import { AcresMapboxLayerIds, AcresMapboxSourceIds } from '@/mapbox-layers';
import getSoilLayer from '@/mapbox-layers/soils';
import { ReactSetState } from '@/models/reactTypes';
import { SoilIndexHumanReadable } from '@/models/soil/soil-type';
import { SoilFeature } from '@/models/soils';
import { useGetSoilQuery } from '@/redux/api/itascaserverApi';
import { RootState } from '@/redux/store';
import addLayer from '@/utils/addLayer';
import { categoricalPaint, getColorMap } from '@/utils/colorFunc';
import getGeoJSONSource from '@/utils/getGeoJSONSource';
import { isDefined } from '@/utils/guards';
import { multiUnion } from '@/utils/multiUnion';
import removeLayers from '@/utils/removeLayers';
import { SoilIndex } from '@/utils/uniqueEndpoints';

type Props = {
  map: maplibregl.Map | undefined;
  limitedFeatures?: any[] | null;
  setColorMap?: ReactSetState<{ [musym: string]: string }>;
  activeScoreIndex?: SoilIndex;
};

export const useSoil = ({
  map,
  limitedFeatures = null,
  setColorMap,
  activeScoreIndex,
}: Props) => {
  const { allFeatures } = useSelector((state: RootState) => state.features);
  const [layer, setLayer] = useState<FillLayerSpecification | undefined>(undefined);
  const [currentHoveredId, setCurrentHoveredId] = useState<
    string | number | undefined
  >(undefined);

  const featuresToUse = useMemo(() => {
    return limitedFeatures || allFeatures;
  }, [allFeatures, limitedFeatures]);

  const { debouncedValue: debounceBoundaries, isFresh } = useDebounce(
    featuresToUse,
    800
  );
  const payload = useMemo(
    () => ({
      boundary: multiUnion(debounceBoundaries),
      combinedBoundaries: debounceBoundaries,
    }),
    [debounceBoundaries]
  );

  const unboundedPayload = useMemo(
    () => ({
      boundary: multiUnion(featuresToUse),
    }),
    [featuresToUse]
  );

  const { data, isFetching, isSuccess, isLoading, error } = useGetSoilQuery(
    payload,
    {
      skip: !unboundedPayload.boundary,
    }
  );

  /**
   * If it does not have the latest data, is fetching new soil data, or is initially loading the soil data
   */
  const hasStaleData = useMemo(
    () => !isFresh || isFetching || isLoading,
    [isFetching, isFresh, isLoading]
  );

  const getToolTipForSoilFeature = useCallback(
    (soilFeature: SoilFeature) => {
      const { properties } = soilFeature;

      if (!activeScoreIndex) return properties!.musym;
      return `${properties!.musym} - ${
        SoilIndexHumanReadable[activeScoreIndex]
      }: ${
        properties![activeScoreIndex]
          ? properties![activeScoreIndex].toFixed(1)
          : '-'
      }`;
    },
    [activeScoreIndex]
  );

  const { soilAverages, soilData } =
    useMemo(() => {
      const copy = cloneDeep(data);

      if (!copy) return undefined;

      const soilFeatureCollection = copy.feature_collection;

      soilFeatureCollection.features.forEach((soilFeature, idx) => {
        soilFeatureCollection.features[idx].properties!.tool_tip =
          getToolTipForSoilFeature(soilFeature);
      });
      return {
        soilAverages: copy.soilAverages,
        soilData: soilFeatureCollection,
      };
    }, [data, getToolTipForSoilFeature]) || {};

  /**
   * When new soil data comes in, this function will display the new data on the map
   */
  const addSoilLayer = useCallback(() => {
    if (
      !soilData ||
      !map ||
      hasStaleData ||
      featuresToUse.length === 0 ||
      !unboundedPayload.boundary
    )
      return;

    const source = getGeoJSONSource(map, AcresMapboxSourceIds.SOIL);

    /**
     * Checks if it needs to add/update the soil source
     */
    if (source) {
      source.setData(soilData);
    } else {
      const soilSource: GeoJSONSourceSpecification = {
        data: soilData,
        promoteId: 'mukey',
        type: 'geojson',
      };
      map.addSource(AcresMapboxSourceIds.SOIL, soilSource);
    }

    setColorMap?.(getColorMap('musym', soilData));

    const filter = categoricalPaint('musym', soilData);
    const soilLayer = getSoilLayer(filter);

    setLayer(soilLayer);
    addLayer(map, [soilLayer]);
  }, [
    soilData,
    map,
    hasStaleData,
    featuresToUse,
    unboundedPayload.boundary,
    setColorMap,
  ]);

  const { hoveredId } = useLayerHover(map, layer, layer);

  useEffect(() => {
    setCurrentHoveredId(hoveredId);
  }, [hoveredId]);

  const onHover = useCallback(
    (id: string | number | undefined, isHovered) => {
      if (!map || !isDefined(id)) return;

      if (!isHovered) {
        setCurrentHoveredId(undefined);
      } else {
        setCurrentHoveredId(id);
      }

      map.setFeatureState(
        {
          id,
          source: AcresMapboxSourceIds.SOIL,
        },
        {
          hover: isHovered,
        }
      );
    },
    [map]
  );

  useEffect(() => {
    if (!map) return () => {};

    onHover(currentHoveredId, true);

    return () => {
      onHover(currentHoveredId, false);
    };
  }, [currentHoveredId, map, onHover]);

  useEffect(() => {
    if (!map) return () => {};

    onHover(hoveredId, true);

    return () => {
      onHover(hoveredId, false);
    };
  }, [hoveredId, map, onHover]);

  useLayerToolTipHover({
    formatText: (text: string) => text,
    hoveredLayer: layer,
    map,
    propertyKey: 'tool_tip',
    shouldDisplayToolTip: true,
  });

  /**
   * Adds the soil data onto the map. Will get called whenever new soil data is retrieved
   */
  useEffect(() => {
    addSoilLayer();
  }, [addSoilLayer]);

  /**
   * Removes the soil layer when retrieving new data
   */
  useEffect(() => {
    if (!map) return;

    if (hasStaleData) removeLayers(map, [AcresMapboxLayerIds.SOIL]);
  }, [hasStaleData, map]);

  /**
   * Removes the soil layer on un-mount
   */
  useEffect(
    () => () => {
      if (map) removeLayers(map, [AcresMapboxLayerIds.SOIL]);
    },
    [map]
  );

  return {
    currentHoveredId,
    data,
    error,
    hasStaleData,
    isLoading,
    isSuccess,
    onHover,
    setCurrentHoveredId,
    soilAverages,
    soilData,
    unboundedPayload,
  };
};
